import React, { useState, useEffect } from 'react';
import {
    ClipboardList, PlusCircle, Search, Pencil, Trash2,
    CheckCircle, Clock, XCircle, DollarSign, Building, CheckSquare, Printer, Redo, Loader2
} from 'lucide-react';
import api from '../services/api'; // Agora usa sua API Node.js
import Modal from '../components/Modal';

// Formata telefone para exibição
const formatPhone = (phone) => {
    if (!phone) return '';
    return phone.replace('@s.whatsapp.net', '').replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

const OrdemServicoScreen = ({ currentUser, clients, externalCompanies }) => {
    // Estados de Dados
    const [activeOrders, setActiveOrders] = useState([]);
    const [historyOrders, setHistoryOrders] = useState([]);
    const [loadingActive, setLoadingActive] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Estados de Controle
    const [currentTab, setCurrentTab] = useState('active'); // 'active' | 'history'
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Filtro de Data
    const getLocalYYYYMMDD = (d) => {
        if (!d) return '';
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    const [startDate, setStartDate] = useState(getLocalYYYYMMDD(sevenDaysAgo));
    const [endDate, setEndDate] = useState(getLocalYYYYMMDD(today));

    const initialFormState = {
        id: '',
        openingDate: getLocalYYYYMMDD(new Date()),
        clientName: '',
        clientPhone: '',
        companyName: '',
        requestDescription: '',
        productsUsed: '',
        solutionDescription: '',
        totalValue: '',
        status: 'open',
        protocolNumber: '',
        deadlineDate: '' // 🆕 Previsão de Atendimento
    };
    const [formData, setFormData] = useState(initialFormState);

    // --- 1. CARREGAR DADOS ---
    const fetchOrders = async () => {
        try {
            if (currentTab === 'active') {
                setLoadingActive(true);
                // Busca ativas (open, progress)
                const res = await api.get('/work-orders', { params: { type: 'active' } });
                setActiveOrders(res.data || []);
                setLoadingActive(false);
            } else {
                setLoadingHistory(true);
                // Busca histórico (finished, canceled) com filtro de data
                const res = await api.get('/work-orders', {
                    params: { type: 'history', startDate, endDate }
                });
                setHistoryOrders(res.data || []);
                setLoadingHistory(false);
            }
        } catch (error) {
            console.error("Erro ao buscar O.S.:", error);
            setLoadingActive(false);
            setLoadingHistory(false);
        }
    };

    // Recarrega quando muda a aba ou as datas (se estiver no histórico)
    useEffect(() => {
        fetchOrders();
    }, [currentTab, startDate, endDate]);

    // --- IMPRESSÃO (Mantida igual, apenas ajustada para dados do Mongo) ---
    const handlePrint = (os) => {
        const printWindow = window.open('', '_blank');
        const statusMap = { 'open': 'Aberta', 'progress': 'Em Andamento', 'finished': 'Concluída', 'canceled': 'Cancelada' };
        const protocolDisplay = os.protocolNumber || os._id?.slice(-6).toUpperCase() || 'N/A';

        printWindow.document.write(`
            <html>
            <head>
                <title>O.S. #${protocolDisplay}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: 0 auto; }
                    .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #333; padding-bottom: 20px; }
                    .header h1 { margin: 0; font-size: 28px; color: #000; }
                    .header p { margin: 5px 0 0; font-size: 14px; color: #666; }
                    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; background: #f9f9f9; padding: 20px; border-radius: 8px; border: 1px solid #eee; }
                    .label { font-size: 12px; color: #666; text-transform: uppercase; font-weight: bold; }
                    .value { font-size: 16px; margin-bottom: 10px; display: block; }
                    .section { margin-bottom: 25px; }
                    .section-title { font-size: 14px; font-weight: bold; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-bottom: 10px; text-transform: uppercase; color: #000; }
                    .box { border: 1px solid #ddd; padding: 15px; min-height: 60px; border-radius: 4px; background: #fff; white-space: pre-wrap; }
                    .total-section { text-align: right; margin-top: 30px; font-size: 24px; font-weight: bold; color: #000; }
                    .footer { margin-top: 80px; display: flex; justify-content: space-between; text-align: center; }
                    .signature { border-top: 1px solid #000; width: 45%; padding-top: 10px; font-size: 12px; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>ORDEM DE SERVIÇO</h1>
                    <p>Protocolo: <strong>#${protocolDisplay}</strong> | Data: ${new Date(os.openingDate).toLocaleDateString('pt-BR')}</p>
                </div>
                <div class="info-grid">
                    <div>
                        <span class="label">Cliente</span><span class="value">${os.clientName}</span>
                        <span class="label">Telefone</span><span class="value">${formatPhone(os.clientPhone) || '-'}</span>
                    </div>
                    <div style="text-align: right;">
                        <span class="label">Empresa</span><span class="value">${os.companyName || 'Particular'}</span>
                        <span class="label">Status Atual</span><span class="value">${statusMap[os.status] || os.status}</span>
                    </div>
                </div>
                <div class="section"><div class="section-title">Solicitação</div><div class="box">${os.requestDescription || 'Não informado.'}</div></div>
                <div class="section"><div class="section-title">Produtos Utilizados</div><div class="box">${os.productsUsed || 'Nenhum.'}</div></div>
                <div class="section"><div class="section-title">Solução Técnica</div><div class="box">${os.solutionDescription || 'Em análise / Pendente.'}</div></div>
                <div class="total-section">Total: ${parseFloat(os.totalValue || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                <div class="footer"><div class="signature">Assinatura do Cliente</div><div class="signature">Técnico Responsável</div></div>
                <script>window.onload = function() { window.print(); window.close(); }</script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    // --- AÇÕES CRUD ---
    const handleSave = async (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            totalValue: parseFloat(formData.totalValue.toString().replace(',', '.')) || 0
        };

        try {
            if (isEditing && formData.id) {
                await api.put(`/work-orders/${formData.id}`, payload);
                alert("Atualizado com sucesso!");
            } else {
                await api.post('/work-orders', payload);
                alert("Criado com sucesso!");
            }
            setModalOpen(false);
            setFormData(initialFormState);
            setIsEditing(false);
            fetchOrders();
        } catch (error) { console.error(error); alert("Erro ao salvar."); }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Excluir permanentemente esta O.S.?")) {
            try {
                await api.delete(`/work-orders/${id}`);
                fetchOrders();
            } catch (error) { alert("Erro ao excluir."); }
        }
    };

    const handleFinish = async (id) => {
        if (window.confirm("Deseja finalizar esta O.S.?")) {
            try {
                await api.put(`/work-orders/${id}`, { status: 'finished' });
                alert("O.S. Finalizada com sucesso!");
                fetchOrders();
            } catch (error) { alert("Erro ao finalizar."); }
        }
    };

    const handleReopen = async (id) => {
        if (window.confirm("Deseja REABRIR esta O.S.?")) {
            try {
                await api.put(`/work-orders/${id}`, { status: 'open' });
                alert("O.S. Reaberta!");
                setCurrentTab('active'); // Muda para a aba ativa para ver a OS
                fetchOrders();
            } catch (error) { alert("Erro ao reabrir."); }
        }
    };

    const handleEdit = (order) => {
        setFormData({
            ...order,
            id: order._id || order.id,
            openingDate: order.openingDate ? getLocalYYYYMMDD(new Date(order.openingDate)) : (order.createdAt ? getLocalYYYYMMDD(new Date(order.createdAt)) : ''),
            deadlineDate: order.deadlineDate ? getLocalYYYYMMDD(new Date(order.deadlineDate)) : ''
        });
        setIsEditing(true);
        setModalOpen(true);
    };

    const handleClientSelect = (e) => {
        const clientName = e.target.value;
        const client = clients.find(c => c.name === clientName);
        if (client) {
            setFormData(prev => ({
                ...prev,
                clientName: client.name,
                clientPhone: client.phone,
                companyName: client.company || prev.companyName
            }));
        } else {
            setFormData(prev => ({ ...prev, clientName: clientName }));
        }
    };

    const filterList = (list) => {
        const lower = searchTerm.toLowerCase();
        return list.filter(o =>
            (o.clientName?.toLowerCase() || '').includes(lower) ||
            (o.companyName?.toLowerCase() || '').includes(lower) ||
            (o.protocolNumber?.toLowerCase() || '').includes(lower)
        );
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'open': return <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-[10px] flex items-center w-fit font-bold border border-yellow-200"><Clock className="w-3 h-3 mr-1" /> Aberta</span>;
            case 'progress': return <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-[10px] flex items-center w-fit font-bold border border-blue-200"><Clock className="w-3 h-3 mr-1" /> Andamento</span>;
            case 'finished': return <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-[10px] flex items-center w-fit font-bold border border-green-200"><CheckCircle className="w-3 h-3 mr-1" /> Concluída</span>;
            case 'canceled': return <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-[10px] flex items-center w-fit font-bold border border-red-200"><XCircle className="w-3 h-3 mr-1" /> Cancelada</span>;
            default: return null;
        }
    };

    const OrdersTable = ({ data, type, emptyMessage }) => (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-600 uppercase font-bold border-b">
                        <tr>
                            <th className="px-4 py-3">Data / Protocolo</th>
                            <th className="px-4 py-3">Cliente</th>
                            <th className="px-4 py-3">Solicitação</th>
                            <th className="px-4 py-3 text-center">Previsão</th>
                            <th className="px-4 py-3 text-center">Valor</th>
                            <th className="px-4 py-3 text-center">Status</th>
                            {type === 'history' && <th className="px-4 py-3 text-center">Encerrado em</th>}
                            <th className="px-4 py-3 text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.length === 0 ? (<tr><td colSpan={type === 'history' ? 7 : 6} className="p-8 text-center text-gray-500">{emptyMessage}</td></tr>) : (
                            data.map(os => (
                                <tr key={os._id || os.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <p className="font-bold text-gray-800">
                                            {os.openingDate && !isNaN(new Date(os.openingDate))
                                                ? new Date(os.openingDate).toLocaleDateString('pt-BR')
                                                : (os.createdAt && !isNaN(new Date(os.createdAt))
                                                    ? new Date(os.createdAt).toLocaleDateString('pt-BR')
                                                    : '-')}
                                        </p>
                                        <p className="text-[10px] text-blue-600 font-mono font-bold">#{os.protocolNumber || 'N/A'}</p>
                                    </td>
                                    <td className="px-4 py-3"><div className="font-medium text-gray-900 truncate max-w-[150px]">{os.clientName}</div><div className="text-[10px] text-gray-500 flex items-center gap-1"><Building className="w-3 h-3" /> {os.companyName || 'Particular'}</div></td>
                                    <td className="px-4 py-3 max-w-[200px]"><p className="truncate text-gray-600 block" title={os.requestDescription}>{os.requestDescription}</p></td>
                                    <td className="px-4 py-3 text-center text-xs">
                                        {os.deadlineDate ? (
                                            <span className={`font-bold ${new Date(os.deadlineDate) < new Date() && ['open', 'progress'].includes(os.status) ? 'text-red-600' : 'text-gray-600'}`}>
                                                {new Date(os.deadlineDate).toLocaleDateString('pt-BR')}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-center font-bold text-green-600 text-xs">{os.totalValue ? os.totalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</td>
                                    <td className="px-4 py-3 flex justify-center">{getStatusBadge(os.status)}</td>
                                    {type === 'history' && (<td className="px-4 py-3 text-center text-xs text-gray-500">{os.updatedAt ? new Date(os.updatedAt).toLocaleDateString() : '-'}</td>)}
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end space-x-1">
                                            <button onClick={() => handlePrint(os)} className="text-gray-600 hover:bg-gray-100 p-1.5 rounded-full" title="Imprimir"><Printer className="w-4 h-4" /></button>
                                            {type === 'active' && (<button onClick={() => handleFinish(os._id || os.id)} className="text-green-600 hover:bg-green-50 p-1.5 rounded-full" title="Finalizar"><CheckSquare className="w-4 h-4" /></button>)}
                                            {type === 'history' && (<button onClick={() => handleReopen(os._id || os.id)} className="text-orange-500 hover:bg-orange-50 p-1.5 rounded-full" title="Reabrir"><Redo className="w-4 h-4" /></button>)}
                                            <button onClick={() => handleEdit(os)} className="text-blue-500 hover:bg-blue-50 p-1.5 rounded-full" title="Editar"><Pencil className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(os._id || os.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-full" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="p-4 md:p-8 h-full overflow-y-auto bg-gray-50">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div><h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center"><ClipboardList className="w-6 h-6 mr-2 text-blue-600" /> Ordens de Serviço</h1></div>
                <button onClick={() => { setFormData(initialFormState); setIsEditing(false); setModalOpen(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center font-medium shadow-sm text-sm"><PlusCircle className="w-4 h-4 mr-2" /> Nova O.S.</button>
            </div>

            <div className="mb-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-end gap-4 pb-2">
                <nav className="-mb-px flex space-x-8">
                    <button onClick={() => setCurrentTab('active')} className={`pb-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${currentTab === 'active' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}><Clock className="w-4 h-4 mr-2" /> Em Andamento ({activeOrders.length})</button>
                    <button onClick={() => setCurrentTab('history')} className={`pb-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${currentTab === 'history' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}><CheckCircle className="w-4 h-4 mr-2" /> Histórico</button>
                </nav>

                <div className="w-full md:w-auto flex flex-col md:flex-row gap-3 items-end">
                    <div className="relative w-full md:w-64">
                        <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-gray-50 focus:bg-white transition-colors" />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                    {currentTab === 'history' && (
                        <div className="flex gap-2">
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-1.5 border rounded-md text-sm bg-gray-50" title="Data Inicial" />
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-1.5 border rounded-md text-sm bg-gray-50" title="Data Final" />
                        </div>
                    )}
                </div>
            </div>

            {currentTab === 'active' ? (
                loadingActive ? <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" /></div> : <OrdersTable data={filterList(activeOrders)} type="active" emptyMessage="Nenhuma ordem em andamento." />
            ) : (
                loadingHistory ? <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" /></div> : <OrdersTable data={filterList(historyOrders)} type="history" emptyMessage="Nenhuma ordem finalizada no período selecionado." />
            )}

            <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={isEditing ? "Editar O.S." : "Nova O.S."} confirmText="Salvar O.S." onConfirm={handleSave}>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2 pb-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Data de Abertura</label>
                            <input type="date" required value={formData.openingDate || ''} disabled className="w-full p-2 border rounded-md text-sm bg-gray-100 cursor-not-allowed text-gray-500" title="A data de abertura não pode ser editada" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label>
                            <select value={formData.status || 'open'} onChange={e => setFormData({ ...formData, status: e.target.value })} className="w-full p-2 border rounded-md bg-white text-sm">
                                <option value="open">Aberta</option>
                                <option value="progress">Em Andamento</option>
                                <option value="finished">Concluída</option>
                                <option value="canceled">Cancelada</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Previsão de Atendimento (Opcional)</label>
                        <input type="date" value={formData.deadlineDate || ''} onChange={e => setFormData({ ...formData, deadlineDate: e.target.value })} className="w-full p-2 border rounded-md text-sm" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cliente</label>
                            <input list="clients-list" type="text" placeholder="Nome do Cliente" value={formData.clientName || ''} onChange={handleClientSelect} className="w-full p-2 border rounded-md text-sm" required />
                            <datalist id="clients-list">{clients.map(c => <option key={c.id || c._id} value={c.name} />)}</datalist>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Telefone</label>
                            <input type="text" placeholder="(00) 00000-0000" value={formData.clientPhone || ''} onChange={e => setFormData({ ...formData, clientPhone: e.target.value })} className="w-full p-2 border rounded-md text-sm" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Empresa Cliente</label>
                        <input list="companies-list" type="text" placeholder="Empresa" value={formData.companyName || ''} onChange={e => setFormData({ ...formData, companyName: e.target.value })} className="w-full p-2 border rounded-md text-sm" />
                        <datalist id="companies-list">{externalCompanies && externalCompanies.map(c => <option key={c.id || c._id} value={c.name} />)}</datalist>
                    </div>

                    <hr className="border-gray-100" />

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Solicitação / Descrição</label>
                        <textarea rows="2" value={formData.requestDescription || ''} onChange={e => setFormData({ ...formData, requestDescription: e.target.value })} className="w-full p-2 border rounded-md text-sm" placeholder="Descrição..."></textarea>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Produtos Utilizados</label>
                        <textarea rows="1" value={formData.productsUsed || ''} onChange={e => setFormData({ ...formData, productsUsed: e.target.value })} className="w-full p-2 border rounded-md text-sm" placeholder="Peças..."></textarea>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Solução Realizada</label>
                        <textarea rows="2" value={formData.solutionDescription || ''} onChange={e => setFormData({ ...formData, solutionDescription: e.target.value })} className="w-full p-2 border rounded-md text-sm" placeholder="Solução..."></textarea>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Valor Total</label>
                        <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <input type="number" step="0.01" value={formData.totalValue || ''} onChange={e => setFormData({ ...formData, totalValue: e.target.value })} className="w-full pl-9 p-2 border rounded-md font-bold text-green-700 text-sm" placeholder="0,00" />
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default OrdemServicoScreen;